/**
 * Portfolio API
 *
 * API functions for managing contractor portfolio items
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

export interface PortfolioItem {
  id: string;
  contractorId: string;
  title: string;
  description: string | null;
  images: string[];
  order: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreatePortfolioItemDto {
  title: string;
  description?: string;
  images: string[];
}

export interface UpdatePortfolioItemDto {
  title?: string;
  description?: string;
  images?: string[];
  order?: number;
}

export interface ReorderPortfolioDto {
  itemIds: string[];
}

/**
 * Get my portfolio items
 */
export async function getMyPortfolio(): Promise<PortfolioItem[]> {
  const url = buildApiUrl('/contractors/me/portfolio');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<PortfolioItem[]>(response);
}

/**
 * Create a portfolio item
 */
export async function createPortfolioItem(
  dto: CreatePortfolioItemDto
): Promise<PortfolioItem> {
  const url = buildApiUrl('/contractors/me/portfolio');

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<PortfolioItem>(response);
}

/**
 * Update a portfolio item
 */
export async function updatePortfolioItem(
  itemId: string,
  dto: UpdatePortfolioItemDto
): Promise<PortfolioItem> {
  const url = buildApiUrl(`/contractors/me/portfolio/${itemId}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<PortfolioItem>(response);
}

/**
 * Delete a portfolio item
 */
export async function deletePortfolioItem(itemId: string): Promise<void> {
  const url = buildApiUrl(`/contractors/me/portfolio/${itemId}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  await handleApiResponse<void>(response);
}

/**
 * Reorder portfolio items
 */
export async function reorderPortfolio(
  dto: ReorderPortfolioDto
): Promise<PortfolioItem[]> {
  const url = buildApiUrl('/contractors/me/portfolio/reorder');

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<PortfolioItem[]>(response);
}
